import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PricingBreakdown, PricingBreakdownItem } from "../../../../../../util/pricing";
import { Product } from '../../../resources/product';
import { OrderDetailService, fetchLatest, } from '../order-detail.service';
import { MatDialog } from '@angular/material/dialog';
import { exportCsv } from 'util/createCsv';

@Component({
  selector: 'order-detail-pricing-breakdown',
  templateUrl: './order-detail-pricing-breakdown.component.html',
  styleUrls: ['./order-detail-pricing-breakdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderDetailPricingBreakdownComponent implements OnInit, OnDestroy {

  constructor(
      public service: OrderDetailService,
      private dialog: MatDialog,
  ) { }

  public ready: BehaviorSubject<boolean>;
  public topLevelIds: string[];
  public topLevelProducts: { [key: string]: Observable<Product> };
  public topLevelQtys: { [key: string]: BehaviorSubject<number> };
  public toggles: { [key: string]: BehaviorSubject<boolean> };
  public breakdowns: { [key: string]: BehaviorSubject<PricingBreakdown> };
  public productTotals: { [key: string]: BehaviorSubject<number> };
  public exportChecks: BehaviorSubject<{ [key: string]: {all: boolean, [key: number]: boolean} }>;

  async ngOnInit() {
    this.ready = new BehaviorSubject(false);
    this.topLevelProducts = {};
    this.toggles = {};
    this.topLevelQtys = {};
    this.breakdowns = {};
    this.productTotals = {};
    this.exportChecks = new BehaviorSubject({});

    // Make sure these preserve order, they get shuffled in the topLevelProducts object
    const order = await fetchLatest(this.service.postChangesOrder$.pipe(filter(o => !!o)));

    this.topLevelIds = order?.products.map((p) => p.productProductId);

    // Collect the observables for the top-level products only. We need these immediately
    for (let productId of this.topLevelIds) {
      const observable = this.service.getProductObservable(productId);
      this.topLevelProducts[productId] = observable;
      const product = await fetchLatest(observable);

      // if (!product.quantitiesMap || !product.quantitiesMap.length) {
      //   // If we're missing a quantitiesMap, this will crash when calculating quantities later, so set some reasonable defaults
      //   product.quantitiesMap = [{value: 1, markup: 18, showOnQuote: true}];
      // }

      // Set up BehaviorSubjects for each top-level qty map for the button-toggle-group
      this.topLevelQtys[product.productId] = new BehaviorSubject<number>(product.quantitiesMap[0]?.value ?? null);
    }
    this.ready.next(true);
  }

  public checkToggle(id: string, section: string = '', index: number = -1): BehaviorSubject<boolean> {
    const handle = this.makeHandle(id, section, index);

    if (!(handle in this.toggles)) {
      this.toggles[handle] = new BehaviorSubject<boolean>(false);
    }

    return this.toggles[handle];
  }

  public swapToggle(id: string, section: string = '', index: number = -1) {
    const handle = this.makeHandle(id, section, index);

    if (!(handle in this.toggles)) {
      // Should be impossible to trigger a swap on a toggle that was never checked
      return;
    }

    this.toggles[handle].next(!this.toggles[handle].value);
  }

  public getBreakdown(allProducts: Product[], product: Product, qty: number): BehaviorSubject<PricingBreakdown> {
    const handle = this.makeHandle(product.productId, `${qty}`);

    if (!(handle in this.breakdowns)) {
      const newBreakdown = this.service.getPriceBreakdown(allProducts, product.productId, qty);
      this.breakdowns[handle] = new BehaviorSubject<PricingBreakdown>(newBreakdown);
    }

    return this.breakdowns[handle];
  }

  public getProductTotal(allProducts: Product[], product: Product, qty: number) {
    const handle = this.makeHandle(product.productId, `${qty}`);

    if (!(handle in this.productTotals)) {
      this.setProductTotal(allProducts, product, qty);
    }

    return this.productTotals[handle];
  }

  public getChildrenTotal(allProducts: Product[], children: Product[], qty: number) {
    let total = 0;

    for (const child of children) {
      total += this.getProductTotal(allProducts, child, qty).value;
    }

    return total;
  }

  public setProductTotal(allProducts: Product[], product: Product, qty: number) {
    const handle = this.makeHandle(product.productId, `${qty}`);

    let total = 0;

    // Get direct costs
    const directBreakdown = this.getBreakdown(allProducts, product, qty).value;

    total += Product.getTotalBasePriceFromBreakdown(directBreakdown);

    // Get subassembly costs
    const children = allProducts.filter(p => p.parentAssemblyId === product.productId);
    total += this.getChildrenTotal(allProducts, children, qty);

    if (!(handle in this.productTotals)) {
      this.productTotals[handle] = new BehaviorSubject<number>(total);
    } else {
      this.productTotals[handle].next(total);
    }
  }


  public getCategoryPrice(breakdown: PricingBreakdownItem[]) {
    return breakdown.reduce((acc, cur) => acc + cur.price, 0);
  }

  public getIndent(indent: number) {
    return `${indent*2}%`;
  }

  private makeHandle(id: string, section: string = '', index: number = -1) {
    let handle = id;

    if (section.length)
      handle += '+' + section;

    if (index >= 0)
      handle += '+' + index;

    return handle;
  }

  ngOnDestroy() {
    for (const key in this.topLevelQtys) {
      this.topLevelQtys[key].complete();
    }

    for (const key in this.toggles) {
      this.toggles[key].complete();
    }

    for (const key in this.breakdowns) {
      this.breakdowns[key].complete();
    }

    for (const key in this.productTotals) {
      this.productTotals[key].complete();
    }
  }

  private currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    useGrouping: false,
  });
  // CSV generation
  public async breakdownToArray(allProducts: Product[], productId: string, qtys: number[], depth = 0): Promise<(string|number)[][]> {
    const product = allProducts.find(p => p.productId === productId);
    const prefix = depth > 0 ? "=".repeat(depth) + "> " : "";

    const breakdowns: { qty: number, breakdown: PricingBreakdown }[] = [];
    const rows: (string | number)[][] = [];
    const totalQuantities: {[key: number]: number} = {};
    const extTotals: {[key: number]: number} = {};
    const unitTotals: {[key: number]: number} = {};

    const dollars = (x: number) => (this.currencyFormatter.format(x));

    // Collect all breakdowns
    for (const qty of qtys) {
      const breakdown = this.getBreakdown(allProducts, product, qty)?.value;
      if (!breakdown) continue;

      breakdowns.push({ qty, breakdown });

      totalQuantities[qty] = this.service.getTotalQuantity(allProducts, product.productId, qty);
      extTotals[qty] = 0;
      unitTotals[qty] = 0;
    }

    // Set header row for this product
    const headerRow = [`${prefix}Top-level Quantity`];
    const subheaderRow = [`${prefix}${product.partNumber} Rev. ${product.revision} quantity`]
    for (const item of breakdowns) {
      headerRow.push(`${item.qty}x Ext`);
      headerRow.push(`${item.qty}x Unit`);
      subheaderRow.push(`${totalQuantities[item.qty]}x Ext`);
      subheaderRow.push(`${totalQuantities[item.qty]}x Unit`);
    }

    rows.push(headerRow);
    rows.push(subheaderRow);

    // Material
    if (breakdowns[0].breakdown.material) {
      const materialRow = [`${prefix}Material (${breakdowns[0].breakdown.material.name})`];
      const materialMarkupRow = [`${prefix}Material Markup of ${product.materialMarkup ?? 0}%`]
      for (const item of breakdowns) {
        const preMarkupPriceItem = item.breakdown.material.subitems.find(si => si.id === 'PRE_MARKUP');
        const materialMarkupItem = item.breakdown.material.subitems.find(si => si.id === 'MARKUP_AMT');

        extTotals[item.qty] += preMarkupPriceItem.value;
        unitTotals[item.qty] += preMarkupPriceItem.value / totalQuantities[item.qty];
        materialRow.push(dollars(preMarkupPriceItem.value));
        materialRow.push(dollars(preMarkupPriceItem.value / totalQuantities[item.qty]));

        extTotals[item.qty] += materialMarkupItem.value;
        unitTotals[item.qty] += materialMarkupItem.value / totalQuantities[item.qty];
        materialMarkupRow.push(dollars(materialMarkupItem.value));
        materialMarkupRow.push(dollars(materialMarkupItem.value / totalQuantities[item.qty]));
      }

      rows.push(materialRow);
      rows.push(materialMarkupRow);
    }

    // Purchased items
    if (breakdowns[0].breakdown.purchasedItems) {
      for (const item of breakdowns[0].breakdown.purchasedItems) {

        // Get an array of quantities mapped to that breakdown's version of the currently iterated item
        const allItemPrices = breakdowns.map(b => ({qty: b.qty, item: b.breakdown.purchasedItems.find(i => i.itemId == item.itemId) }));

        // Set row names off the first item
        const itemRow = [`${prefix}${item.name}`];
        const markupRow = [`${prefix}Markup of ${item.subitems.find(si => si.id === 'MARKUP_PCT').value}%`];

        // Write ext/unit prices in order
        for (const itemPrice of allItemPrices) {
          const preMarkupItem = itemPrice.item.subitems.find(si => si.id === 'PARENT_QUANTITY_MULT');

          extTotals[itemPrice.qty] += preMarkupItem.value;
          unitTotals[itemPrice.qty] += preMarkupItem.value / totalQuantities[itemPrice.qty];
          itemRow.push(dollars(preMarkupItem.value));
          itemRow.push(dollars(preMarkupItem.value / totalQuantities[itemPrice.qty]));

          const markupAmountItem = itemPrice.item.subitems.find(si => si.id === 'CALC_MARKUP');

          extTotals[itemPrice.qty] += markupAmountItem.value;
          unitTotals[itemPrice.qty] += markupAmountItem.value / totalQuantities[itemPrice.qty];
          markupRow.push(dollars(markupAmountItem.value));
          markupRow.push(dollars(markupAmountItem.value / totalQuantities[itemPrice.qty]));
        }

        rows.push(itemRow);
        rows.push(markupRow);
      }
    }

    // Outside process
    if (breakdowns[0].breakdown.process) {
      for (const item of breakdowns[0].breakdown.process) {

        // Get an array of quantities mapped to that breakdown's version of the currently iterated item
        const allItemPrices = breakdowns.map(b => ({qty: b.qty, item: b.breakdown.process.find(i => i.itemId == item.itemId) }));

        // Set row names off the first item
        const itemRow = [`${prefix}${item.name}`];
        const markupRow = [`${prefix}Markup of ${item.subitems.find(si => si.id === 'MARKUP_PCT').value}%`];
        const inspectionRow = [`${prefix}Inspection total`];

        // Write ext/unit prices in order
        for (const itemPrice of allItemPrices) {
          const preMarkupItem = itemPrice.item.subitems.find(si => si.id === 'QUANTITY_MULT');

          extTotals[itemPrice.qty] += preMarkupItem.value;
          unitTotals[itemPrice.qty] += preMarkupItem.value / totalQuantities[itemPrice.qty];
          itemRow.push(dollars(preMarkupItem.value));
          itemRow.push(dollars(preMarkupItem.value / totalQuantities[itemPrice.qty]));

          const markupAmountItem = itemPrice.item.subitems.find(si => si.id === 'CALC_MARKUP');

          extTotals[itemPrice.qty] += markupAmountItem.value;
          unitTotals[itemPrice.qty] += markupAmountItem.value / totalQuantities[itemPrice.qty];
          markupRow.push(dollars(markupAmountItem.value));
          markupRow.push(dollars(markupAmountItem.value / totalQuantities[itemPrice.qty]));

          const inspectionAmountItem = itemPrice.item.subitems.find(si => si.id === 'TOTAL_INSPECTION');

          if (inspectionAmountItem && inspectionAmountItem.value > 0) {
            extTotals[itemPrice.qty] += inspectionAmountItem.value;
            unitTotals[itemPrice.qty] += inspectionAmountItem.value / totalQuantities[itemPrice.qty];
            inspectionRow.push(dollars(inspectionAmountItem.value));
            inspectionRow.push(dollars(inspectionAmountItem.value / totalQuantities[itemPrice.qty]));
          }
        }

        rows.push(itemRow);
        rows.push(markupRow);

        if (inspectionRow.length > 1)
          rows.push(inspectionRow);
      }
    }

    // Labor
    if (breakdowns[0].breakdown.labor) {
      for (const item of breakdowns[0].breakdown.labor) {
        const allLaborPrices = breakdowns.map(b => ({qty: b.qty, item: b.breakdown.labor.find(i => i.itemId == item.itemId) }));

        const itemRow = [`${prefix}${item.name}`];

        for (const laborPrice of allLaborPrices) {
          extTotals[laborPrice.qty] += laborPrice.item.price;
          unitTotals[laborPrice.qty] += laborPrice.item.price / totalQuantities[laborPrice.qty];
          itemRow.push(dollars(laborPrice.item.price));
          itemRow.push(dollars(laborPrice.item.price / totalQuantities[laborPrice.qty]));
        }

        rows.push(itemRow);
      }
    }

    // Subassemblies
    const children = await fetchLatest(this.service.getProductChildrenObservable(productId));

    for (const subassembly of children) {
        const subArray = await this.breakdownToArray(allProducts, subassembly.productId, qtys, depth+1);
        rows.push(['']);
        rows.push(...subArray);

        // Parse the results back into numbers and add the totals.
        // First line has quantities and parseInt discards the text on its own.
        // Final line is newline, but next-to-last has totals.
        const quantityRow = subArray[0];
        const totalsRow = subArray[subArray.length - 2];

        for (let i=1; i+1<quantityRow.length; i+=2) {
          const topQty = parseInt(quantityRow[i] as string);
          const extPrice = parseFloat((totalsRow[i] as string).substring(1));
          // Don't read the unit price listed here. The unit totals object is in terms
          // of cost per 1 top-level part, and this unit price is in terms of cost per
          // subassembly. This can be some unknown-from-here multiple. Easier to simply
          // use the extPrice and divide fresh here
          extTotals[topQty] += extPrice;
          unitTotals[topQty] += extPrice / topQty;
        }
    }

    // Add assembly-wide markup
    if (!product.parentAssemblyId) {
      if (product.quantitiesMap.some(q => q?.markup)) {
        // No label on the markup row, will insert it at the end
        const subtotalRow = [`Assembly subtotal for ${product.partNumber} Rev. ${product.revision}`];
        const markupPercent = [`Markup percentage`];
        const markupDollars = [`Markup dollars`];

        for (const item of breakdowns) {
          const markup = product.quantitiesMap.find(q => q.value === item.qty)?.markup;

          // Add subtotals and markup amounts
          if (markup) {
            markupPercent.push(`${markup}%`);
            markupPercent.push(`${markup}%`);

            subtotalRow.push(dollars(extTotals[item.qty]));
            subtotalRow.push(dollars(extTotals[item.qty] / totalQuantities[item.qty]));

            const markupAmount = extTotals[item.qty] * (markup / 100);
            const markupUnitAmount = markupAmount / totalQuantities[item.qty];

            markupDollars.push(dollars(markupAmount));
            markupDollars.push(dollars(markupUnitAmount));

            extTotals[item.qty] += markupAmount;
            unitTotals[item.qty] += markupUnitAmount;
          } else {
            markupPercent.push('0%');
            markupPercent.push('0%');
            subtotalRow.push(dollars(0));
            subtotalRow.push(dollars(0));
            markupDollars.push(dollars(0));
            markupDollars.push(dollars(0));
          }
        }

        rows.push(subtotalRow);
        rows.push(markupPercent);
        rows.push(markupDollars);
      }
    }

    // All categories now present, print totals
    const totalsRow = [`${prefix}${product.parentAssemblyId ? 'Suba' : 'A'}ssembly total for ${product.partNumber} Rev. ${product.revision}`];

    for (const item of breakdowns) {
      totalsRow.push(dollars(extTotals[item.qty]));
      totalsRow.push(dollars(unitTotals[item.qty]));
    }

    rows.push(totalsRow);
    rows.push(['']);

    return rows;
  }

  public async generateCsv(allProducts: Product[]): Promise<void> {
    const sheetData: (string|number)[][] = [];
    const order = await fetchLatest(this.service.postChangesOrder$);
    for (const product of order.products) {
      // Get all selected quantities in an array
      const qtys = Object.entries(this.exportChecks.value[product.productProductId])
        .filter((qtyValue) => qtyValue[0] !== 'all' && qtyValue[1])
        .map((qtyValue) => parseInt(qtyValue[0]));

      // Generate pricing rows for those quantities
      if (qtys.length > 0)
        sheetData.push(...await this.breakdownToArray(allProducts, product.productProductId, qtys));
    }

    const headers = sheetData.shift().map(r => r.toString());

    exportCsv(order.orderNumber, sheetData, headers);
  }

  @ViewChild('csvExportTemplate') csvExportTemplate: TemplateRef<any>;
  public openExportDialog(allProducts: Product[]) {

    // Create checkbox value array with all values
    const checks: { [key: string]: {all: boolean, [key: number]: boolean} } = {};
    for (const product of allProducts.filter(p => !p.parentAssemblyId)) {
      checks[product.productId] = {all: true};

      for (const qty of product.quantitiesMap)
        checks[product.productId][qty.value] = true;
    }

    this.exportChecks.next(checks);

    this.dialog.open(this.csvExportTemplate, {
      width: '800px',
      data: {
        allProducts,
        generateCsv: this.generateCsv.bind(this),
      }
    });
  }

  public exportUpdateProductCheck(productId: string) {
    const checks = this.exportChecks.value;

    // Are any of the items in the row checked?
    const anyAreChecked = Object.entries(checks[productId])
      .filter(c => c[0] !== 'all')
      .some(c => c[1]);

    // Update accordingly
    if (checks[productId].all !== anyAreChecked) {
      checks[productId].all = anyAreChecked;
      this.exportChecks.next(checks);
    }
  }

  public exportToggleProduct(productId: string, target: boolean) {
    // If all are checked, deselect all. If any are unchecked, select all
    const checks = this.exportChecks.value;

    for (const qty of Object.keys(checks[productId]))
      checks[productId][qty] = target;

    this.exportChecks.next(checks);
  }

}
